import React from 'react';
import premiumIcon from '../assets/images/premium_icon.PNG';

/**
 * PremiumFeatureModal - Paywall modal for premium features
 * 
 * Shows when users try to access premium features they don't have access to.
 * Displays feature benefits and upgrade options.
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {string} featureName - Name of the feature (e.g., "Recurring Allocations")
 * @param {string} currentTier - User's current subscription tier
 * @param {array} requiredTiers - Tiers that have access (e.g., ['premium'])
 * @param {array} benefits - Array of benefit strings to display
 * @param {string} upgradeUrl - Optional custom upgrade URL
 */
const PremiumFeatureModal = ({ 
  isOpen, 
  onClose, 
  featureName = "This Feature",
  currentTier = "basic",
  requiredTiers = ["premium"],
  benefits = [],
  upgradeUrl = "/pricing"
}) => {
  if (!isOpen) return null;

  // Default benefits if none provided
  const defaultBenefits = [
    "Save time with automated scheduling",
    "Reduce manual data entry",
    "Ensure consistency across weeks",
    "Perfect for seasonal training schedules"
  ];

  const displayBenefits = benefits.length > 0 ? benefits : defaultBenefits;

  // Tier display names
  const tierNames = {
    basic: "Basic",
    standard: "Standard", 
    premium: "Premium"
  };

  const currentTierName = tierNames[currentTier] || currentTier;
  const requiredTierNames = requiredTiers.map(t => tierNames[t] || t).join(" or ");

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}
        onClick={onClose}
      >
        {/* Modal */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            animation: 'slideUp 0.3s ease-out'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with gradient */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '32px',
            textAlign: 'center',
            position: 'relative'
          }}>
            {/* Close button */}
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                fontSize: '20px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              ×
            </button>

            {/* Premium icon */}
            <div style={{
              width: '80px',
              height: '80px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              padding: '12px'
            }}>
              <img 
                src={premiumIcon} 
                alt="Premium Feature" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>

            <h2 style={{
              color: 'white',
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 8px',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              {featureName}
            </h2>
            
            <div style={{
              display: 'inline-block',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              backdropFilter: 'blur(10px)'
            }}>
              {requiredTierNames} Feature
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '32px' }}>
            {/* Current plan notice */}
            <div style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '20px' }}>ℹ️</span>
              <div>
                <div style={{
                  fontSize: '14px',
                  color: '#92400e',
                  fontWeight: '600'
                }}>
                  You're currently on the {currentTierName} plan
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#b45309',
                  marginTop: '2px'
                }}>
                  Upgrade to {requiredTierNames} to unlock this feature
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '16px'
              }}>
                Why you'll love {featureName}:
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {displayBenefits.map((benefit, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    <span style={{
                      fontSize: '18px',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}>
                      ✓
                    </span>
                    <span style={{
                      fontSize: '14px',
                      color: '#374151',
                      lineHeight: '1.5'
                    }}>
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '24px'
            }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                }}
              >
                Maybe Later
              </button>
              
              <button
                onClick={() => {
                  window.location.href = upgradeUrl;
                }}
                style={{
                  flex: 2,
                  padding: '14px 24px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
              >
                View Plans & Upgrade →
              </button>
            </div>

            {/* Small print */}
            <p style={{
              textAlign: 'center',
              fontSize: '12px',
              color: '#9ca3af',
              marginTop: '16px',
              marginBottom: 0
            }}>
              Upgrade anytime • Cancel anytime • No long-term contracts
            </p>
          </div>
        </div>
      </div>

      {/* Animation */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default PremiumFeatureModal;
